import { createApi } from '@reduxjs/toolkit/query/react';
import { Action } from '../constants/action';
import { ApiResponse } from '../types/api';
import { Tag } from '../types/metricGroup';
import { axiosBaseQuery } from './axiosQuery';

interface MetricGroupsMutationParams {
  initiativeId: string;
  groupId: string;
  initiativeIds: string[];
}

interface DeleteCustomTagsParams extends Pick<MetricGroupsMutationParams, 'initiativeId'> {
  ids: Tag['_id'][];
}

type CreateCustomTagsParams = Pick<Tag, 'groupName'> & Pick<MetricGroupsMutationParams, 'initiativeId'>;

export type SelectedTagIdsType = { [key in Action]: Tag['_id'][] };

interface UpdateCustomTagsParams extends Pick<MetricGroupsMutationParams, 'initiativeId'> {
  updatedTagIds: SelectedTagIdsType;
  utrIds: string[];
}

const transformResponse = <T>(response: ApiResponse<T>) => response.data;

export const metricGroupsApi = createApi({
  reducerPath: 'metricGroupsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['metricGroups', 'customTags'],
  endpoints: (builder) => ({
    addCompaniesToMetricGroup: builder.mutation<ApiResponse, MetricGroupsMutationParams>({
      invalidatesTags: ['metricGroups'],
      query: ({ initiativeId, initiativeIds, groupId }) => ({
        url: `/initiatives/${initiativeId}/metric-groups/${groupId}/share`,
        method: 'patch',
        data: {
          initiativeIds
        }
      }),
    }),
    getCustomTags: builder.query<Tag[], string>({
      transformResponse,
      query: (initiativeId) => ({
        url: `/initiatives/${initiativeId}/metric-groups/tag`,
        method: 'get',
      }),
      providesTags: ['customTags'],
    }),
    createCustomTag: builder.mutation<Tag, CreateCustomTagsParams>({
      invalidatesTags: ['customTags'],
      transformResponse,
      query: ({ initiativeId, ...data }) => {
        return {
          url: `/initiatives/${initiativeId}/metric-groups/tag`,
          method: 'post',
          data,
        };
      },
    }),
    updateCustomTags: builder.mutation<Tag, UpdateCustomTagsParams>({
      invalidatesTags: ['customTags'],
      transformResponse,
      query: ({ initiativeId, ...data }) => {
        return {
          url: `/initiatives/${initiativeId}/metric-groups/tag`,
          method: 'patch',
          data,
        };
      },
    }),
    deleteCustomTags: builder.mutation<Tag, DeleteCustomTagsParams>({
      invalidatesTags: ['customTags'],
      transformResponse,
      query: ({ initiativeId, ids }) => {
        return {
          url: `/initiatives/${initiativeId}/metric-groups/tag`,
          method: 'delete',
          data: { ids },
        };
      },
    }),
  }),
});

export const {
  endpoints,
  useAddCompaniesToMetricGroupMutation,
  useGetCustomTagsQuery,
  useCreateCustomTagMutation,
  useUpdateCustomTagsMutation,
  useDeleteCustomTagsMutation,
} = metricGroupsApi;
